import { z } from 'zod';

/**
 * Validation for image-uid path params. Any uid the gallery surfaces must
 * match this shape. Live media uids look like `wix:42:img-7`; drafts use
 * UUIDs; sales variants use `sales:<uuid>`.
 */
export const IMAGE_UID_RE = /^[A-Za-z0-9_:.-]{1,200}$/;
export const ImageUidSchema = z.string().regex(IMAGE_UID_RE);

/**
 * Per-image, per-workspace state stored in `imageStates/{imageUid}`. Tracks
 * favorited + binned status. The four mutation endpoints all return the
 * full ImageState after the change so the optimistic UI doesn't refetch.
 *
 * Zod schema is the source of truth; the TypeScript type is derived from it.
 */
export const ImageStateSchema = z.object({
  imageUid: ImageUidSchema,
  workspaceId: z.string().min(1),
  favorited: z.boolean(),
  /** ISO 8601 when `favorited` last became true. null when never (or restored). */
  favoritedAt: z.string().datetime().nullable(),
  /** ISO 8601 when binned. null when not in the bin (or restored). */
  binnedAt: z.string().datetime().nullable(),
  /** ISO 8601 — last mutation timestamp. */
  updatedAt: z.string().datetime(),
});

export type ImageState = z.infer<typeof ImageStateSchema>;

export function defaultImageState(imageUid: string, workspaceId: string): ImageState {
  return {
    imageUid,
    workspaceId,
    favorited: false,
    favoritedAt: null,
    binnedAt: null,
    updatedAt: new Date(0).toISOString(),
  };
}
